if (!customElements.get('segastore-collection-tabs')) {
  customElements.define('segastore-collection-tabs', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.tabs = Array.from(this.querySelectorAll('[data-edit-tab]')).filter((tab) => !tab.hasAttribute('data-home-duplicate'));
      this.panels = this.tabs.map((tab) => this.querySelector(tab.getAttribute('href')));
      this.nav = this.querySelector('.segastore-collection-tabs__nav');
      if (!this.nav || !this.tabs.length || this.panels.some((panel) => !panel)) return;
      const pairsMatch = this.tabs.every((tab, index) => tab.getAttribute('href') === `#${this.panels[index].id}`);
      if (!pairsMatch) return;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.nav.setAttribute('role', 'tablist');
      this.setupScrollControls(signal);
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[index].id);
        this.panels[index].setAttribute('role', 'tabpanel');
        this.panels[index].setAttribute('aria-labelledby', tab.id);
        this.panels[index].tabIndex = 0;
      });

      this.nav.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-edit-tab]');
        if (!tab || !this.tabs.includes(tab) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.select(this.tabs.indexOf(tab), true);
      }, { signal });

      this.nav.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index === -1) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        let next;
        if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this.select(next, true);
        } else if (event.key === ' ') {
          event.preventDefault();
          this.select(index, true);
        }
      }, { signal });

      document.addEventListener('shopify:block:select', (event) => {
        const panel = this.panels.find((item) => item.dataset.blockId === event.detail?.blockId);
        if (panel) this.select(this.panels.indexOf(panel));
      }, { signal });

      window.addEventListener('hashchange', () => {
        const index = this.panels.findIndex((panel) => `#${panel.id}` === window.location.hash);
        if (index >= 0) this.select(index);
      }, { signal });

      const hashIndex = this.panels.findIndex((panel) => `#${panel.id}` === window.location.hash);
      const previousIndex = this.tabs.findIndex((tab) => tab.id === this.previousTabId);
      this.select(previousIndex >= 0 ? previousIndex : hashIndex >= 0 ? hashIndex : 0);
      this.classList.add('is-enhanced');
      this.updateScrollControls();
      this.prepareDeferredProducts();
    }

    prepareDeferredProducts() {
      if (!this.querySelector('[data-edit-products-pending]')) return;
      if ('IntersectionObserver' in window) {
        this.productsObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadDeferredProducts();
        }, { rootMargin: '600px' });
        this.productsObserver.observe(this);
      } else {
        this.loadDeferredProducts();
      }
      this.addEventListener('focusin', () => this.loadDeferredProducts(), { signal: this.controller.signal });
      this.addEventListener('pointerenter', () => this.loadDeferredProducts(), { signal: this.controller.signal });
    }

    loadDeferredProducts() {
      const pending = [...this.querySelectorAll('[data-edit-products-pending]')];
      if (!pending.length || this.productsRequest || Date.now() < (this.productsRetryAt || 0)) return this.productsRequest;
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.hash = '';
      pending.forEach((grid) => grid.setAttribute('aria-busy', 'true'));
      this.productsRequest = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('Collection products unavailable');
          return response.text();
        })
        .then((html) => {
          if (!this.isConnected) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const replacements = pending.map((grid) => {
            const panel = grid.closest('[data-edit-panel]');
            const source = doc.getElementById(panel.id)?.querySelector('.segastore-collection-tabs__grid');
            if (!source || source.hasAttribute('data-edit-products-pending') || !source.querySelector('[data-product-handle]')) throw new Error('Incomplete collection products');
            return [grid, source];
          });
          replacements.forEach(([grid, source]) => {
            grid.replaceChildren(...source.childNodes);
            grid.removeAttribute('data-edit-products-pending');
            grid.style.removeProperty('min-height');
          });
          this.productsObserver?.disconnect();
        })
        .catch(() => {
          this.productsRetryAt = Date.now() + 5000;
          pending.forEach((grid) => {
            const link = grid.closest('[data-edit-panel]')?.querySelector('.segastore-collection-tabs__view-all');
            const status = grid.querySelector('[role="status"]');
            if (status && link) {
              status.classList.remove('visually-hidden');
              status.replaceChildren(link.cloneNode(true));
              grid.style.minHeight = '0';
            }
          });
        })
        .finally(() => {
          pending.forEach((grid) => grid.removeAttribute('aria-busy'));
          this.productsRequest = null;
        });
      return this.productsRequest;
    }

    setupScrollControls(signal) {
      this.scrollButtons = Array.from(this.querySelectorAll('[data-edit-scroll]'));
      this.queueScrollControls = () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => this.updateScrollControls());
      };
      this.scrollButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.editScroll === 'next' ? 1 : -1;
          const rtl = getComputedStyle(this.nav).direction === 'rtl';
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.nav.scrollBy({
            left: direction * (rtl ? -1 : 1) * Math.max(this.nav.clientWidth * .8, 80),
            behavior: reducedMotion ? 'instant' : 'smooth'
          });
        }, { signal });
      });
      this.nav.addEventListener('scroll', this.queueScrollControls, { passive: true, signal });
      window.addEventListener('resize', this.queueScrollControls, { passive: true, signal });
      if ('ResizeObserver' in window) {
        this.scrollObserver = new ResizeObserver(this.queueScrollControls);
        this.scrollObserver.observe(this.nav);
      }
    }

    updateScrollControls() {
      if (!this.nav || !this.scrollButtons?.length) return;
      const maxScroll = Math.max(0, this.nav.scrollWidth - this.nav.clientWidth);
      const overflow = maxScroll > 2;
      const position = Math.min(maxScroll, Math.max(0, Math.abs(this.nav.scrollLeft)));
      this.scrollButtons.forEach((button) => {
        button.hidden = !overflow;
        button.disabled = button.dataset.editScroll === 'previous' ? position <= 2 : position >= maxScroll - 2;
      });
    }

    refreshCollections() {
      this.previousTabId = this.tabs?.find((tab) => tab.getAttribute('aria-selected') === 'true')?.id;
      this.disconnectedCallback();
      this.connectedCallback();
    }

    select(index, focus = false) {
      if (this.panels[index]?.querySelector('[data-edit-products-pending]')) this.loadDeferredProducts();
      this.tabs.forEach((tab, tabIndex) => {
        const active = index === tabIndex;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[tabIndex].hidden = !active;
      });
      if (focus) {
        this.tabs[index].focus({ preventScroll: true });
        this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      }
    }

    disconnectedCallback() {
      this.productsObserver?.disconnect();
      this.scrollObserver?.disconnect();
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollButtons?.forEach((button) => { button.hidden = true; });
      this.controller?.abort();
      this.controller = null;
      this.classList.remove('is-enhanced');
      this.nav?.removeAttribute('role');
      this.tabs?.forEach((tab) => {
        ['role', 'aria-controls', 'aria-selected', 'tabindex'].forEach((name) => tab.removeAttribute(name));
      });
      this.panels?.forEach((panel) => {
        panel.hidden = panel.hasAttribute('data-home-duplicate');
        ['role', 'aria-labelledby', 'tabindex'].forEach((name) => panel.removeAttribute(name));
      });
    }
  });
}
